import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { VolumePage } from '../data/volumeOnePages';
type PageLightboxProps = {
  pages: VolumePage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function PageLightbox({
  pages,
  index,
  onClose,
  onChange
}: PageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null;
  const prev = () => {
    if (index === null) return;
    onChange((index - 1 + pages.length) % pages.length);
  };
  const next = () => {
    if (index === null) return;
    onChange((index + 1) % pages.length);
  };
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);
  const page = index !== null ? pages[index] : null;
  return <AnimatePresence data-id="element-175" data-mp="hjxd99n4yxqc">
      {open && page && <motion.div role="dialog" aria-modal="true" aria-label={page.caption} className="fixed inset-0 z-[60] flex items-center justify-center bg-plum-deep/90 px-4 py-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onClick={onClose} data-id="element-176" data-mp="mgnrrsjall9n">
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-177" data-mp="2sog8fgwma36">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-178" data-mp="1auu0j9k4xvbk" />
          </button>

          <button type="button" onClick={e => {
        e.stopPropagation();
        prev();
      }} aria-label="Previous page" className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold sm:left-6" data-id="element-179" data-mp="1awx0omf054u0">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-180" data-mp="27jryf0n6m9cy" />
          </button>

          <motion.figure className="flex max-h-full flex-col items-center" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease
      }} onClick={e => e.stopPropagation()} data-id="element-181" data-mp="23ig5al53n600">
            <img src={page.src} alt={page.caption} className="max-h-[82vh] w-auto max-w-[88vw] rounded-2xl bg-white object-contain shadow-cover" data-id="element-182" data-mp="216xjfh7hwtxg" />
            <figcaption className="mt-3 text-center text-sm text-white/80" data-id="element-183" data-mp="2tvl69h7w7rd4">
              {page.caption}
              <span className="ml-2 text-white/50" data-id="element-184" data-mp="2yufq8mhljlyt">
                {index! + 1} / {pages.length}
              </span>
            </figcaption>
          </motion.figure>

          <button type="button" onClick={e => {
        e.stopPropagation();
        next();
      }} aria-label="Next page" className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold sm:right-6" data-id="element-185" data-mp="2p177r5xxp448">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-186" data-mp="1jw1z50na2eci" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}